import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './ascii-table.component.module.scss';

const CONTROL_NAMES: Record<number, { abbr: string; name: string }> = {
    0: { abbr: 'NUL', name: 'Null' },
    1: { abbr: 'SOH', name: 'Start of Heading' },
    2: { abbr: 'STX', name: 'Start of Text' },
    3: { abbr: 'ETX', name: 'End of Text' },
    4: { abbr: 'EOT', name: 'End of Transmission' },
    5: { abbr: 'ENQ', name: 'Enquiry' },
    6: { abbr: 'ACK', name: 'Acknowledge' },
    7: { abbr: 'BEL', name: 'Bell' },
    8: { abbr: 'BS', name: 'Backspace' },
    9: { abbr: 'HT', name: 'Horizontal Tab' },
    10: { abbr: 'LF', name: 'Line Feed' },
    11: { abbr: 'VT', name: 'Vertical Tab' },
    12: { abbr: 'FF', name: 'Form Feed' },
    13: { abbr: 'CR', name: 'Carriage Return' },
    14: { abbr: 'SO', name: 'Shift Out' },
    15: { abbr: 'SI', name: 'Shift In' },
    16: { abbr: 'DLE', name: 'Data Link Escape' },
    17: { abbr: 'DC1', name: 'Device Control 1 (XON)' },
    18: { abbr: 'DC2', name: 'Device Control 2' },
    19: { abbr: 'DC3', name: 'Device Control 3 (XOFF)' },
    20: { abbr: 'DC4', name: 'Device Control 4' },
    21: { abbr: 'NAK', name: 'Negative Acknowledge' },
    22: { abbr: 'SYN', name: 'Synchronous Idle' },
    23: { abbr: 'ETB', name: 'End of Trans. Block' },
    24: { abbr: 'CAN', name: 'Cancel' },
    25: { abbr: 'EM', name: 'End of Medium' },
    26: { abbr: 'SUB', name: 'Substitute' },
    27: { abbr: 'ESC', name: 'Escape' },
    28: { abbr: 'FS', name: 'File Separator' },
    29: { abbr: 'GS', name: 'Group Separator' },
    30: { abbr: 'RS', name: 'Record Separator' },
    31: { abbr: 'US', name: 'Unit Separator' },
    127: { abbr: 'DEL', name: 'Delete' },
};

type Section = 'control' | 'printable' | 'extended';

interface State {
    section: Section;
    search: string;
}

interface Row {
    dec: number;
    hex: string;
    oct: string;
    bin: string;
    char: string;
    name: string;
    isControl: boolean;
}

function makeRow(dec: number): Row {
    const ctrl = CONTROL_NAMES[dec];
    return {
        dec,
        hex: dec.toString(16).padStart(2, '0').toUpperCase(),
        oct: dec.toString(8).padStart(3, '0'),
        bin: dec.toString(2).padStart(8, '0'),
        char: ctrl ? ctrl.abbr : (dec >= 32 && dec < 127 ? String.fromCharCode(dec) : (dec >= 128 ? String.fromCharCode(dec) : '')),
        name: ctrl ? ctrl.name : (dec === 32 ? 'Space' : (dec >= 33 && dec < 127 ? `Printable: ${String.fromCharCode(dec)}` : `Extended (Latin-1): ${String.fromCharCode(dec)}`)),
        isControl: !!ctrl,
    };
}

const ALL_ROWS: Row[] = Array.from({ length: 256 }, (_, i) => makeRow(i));

export default class AsciiTableComponent extends Component<{ feature?: string }, State> {
    state: State = { section: 'printable', search: '' };

    private get filtered(): Row[] {
        const { section, search } = this.state;
        const q = search.trim().toLowerCase();
        const inSection = (r: Row) => {
            if (section === 'control') return r.dec < 32 || r.dec === 127;
            if (section === 'printable') return r.dec >= 32 && r.dec < 127 && r.dec !== 127;
            return r.dec >= 128;
        };
        return ALL_ROWS.filter(r => {
            if (!inSection(r)) return false;
            if (!q) return true;
            return String(r.dec) === q
                || r.hex.toLowerCase() === q
                || r.char.toLowerCase().includes(q)
                || r.name.toLowerCase().includes(q);
        });
    }

    render() {
        const { section, search } = this.state;
        const rows = this.filtered;
        return (
            <div className="row">
                <div className="col-12">
                    <div className={styles.controls}>
                        <input className={styles.search} type="search"
                            placeholder="Search by char, decimal, hex, name…"
                            value={search} onChange={e => this.setState({ search: e.target.value })}
                            aria-label="Filter ASCII characters" />
                        <div className={styles.tabs} role="tablist">
                            {(['control', 'printable', 'extended'] as Section[]).map(s => (
                                <button key={s} role="tab"
                                    className={`${styles.tab} ${section === s ? styles.tabActive : ''}`}
                                    onClick={() => this.setState({ section: s })}
                                    aria-selected={section === s}>
                                    {s === 'control' && 'Control (0–31, 127)'}
                                    {s === 'printable' && 'Printable (32–126)'}
                                    {s === 'extended' && 'Extended (128–255, Latin-1)'}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <colgroup>
                                <col className={styles.colDec} />
                                <col className={styles.colHex} />
                                <col className={styles.colOct} />
                                <col className={styles.colBin} />
                                <col className={styles.colChar} />
                                <col className={styles.colName} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Dec</th>
                                    <th>Hex</th>
                                    <th className={styles.colOct}>Oct</th>
                                    <th className={styles.colBin}>Binary</th>
                                    <th>Char</th>
                                    <th>Name / description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.dec} className={r.isControl ? styles.rowControl : ''}>
                                        <td className={styles.numCell}>{r.dec}</td>
                                        <td className={styles.numCell}>0x{r.hex}</td>
                                        <td className={`${styles.numCell} ${styles.colOct}`}>{r.oct}</td>
                                        <td className={`${styles.numCell} ${styles.colBin}`}>{r.bin}</td>
                                        <td className={styles.charCell}>{r.char || ' '}</td>
                                        <td>{r.name}</td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr><td colSpan={6} className={styles.empty}>No characters match your filter.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p className={sharedStyles.label} style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        Showing {rows.length} characters. Standard ASCII is 0–127; 128–255 shown as Latin-1 (ISO 8859-1) for reference.
                    </p>
                </div>
            </div>
        );
    }
}
